/* eslint-disable react/no-multi-comp */
import * as React from "react";
import { connect } from "react-redux";

import { Alert, Button, Group, TextInput, Tooltip } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bindActionCreators } from "redux";
import { getPersonsByIds } from "../../../data/actions/personActions";
import {
  removeShiftEnrolment,
  updateShiftEnrolment,
} from "../../../data/actions/shiftActions";
import { IState } from "../../../data/reducers/state";
import { Role } from "../../../types/persons/Role";
import { IPerson } from "../../../types/persons/person";
import { IShiftEnrolment } from "../../../types/shifts/ShiftEnrolment";
import { ShiftEnrolmentType } from "../../../types/shifts/enums/ShiftEnrolmentType";
import { SSTable } from "../../common/TableData";
import enrolmentCells from "./ShiftEditor/ShiftEnrolmentCell";
import { roleIconColors, roleIconConfig } from "./ShiftEditor/roleIcons";

const styles: { [key: string]: React.CSSProperties } = {
  root: {
    marginTop: "10px",
  },
  card: {
    margin: "10px",
  },
  maxVertical: {
    overflowX: "auto",
  },
  subtitle: {
    color: "gray",
    fontSize: "12px",
    marginBottom: "0px",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "12px",
  },
  roleFilters: {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  },
  filterSummary: {
    fontSize: "0.85rem",
    color: "var(--color-text-medium)",
  },
};

interface IShiftPersonsEditorProps {
  personToScrollTo: string | null;
  shiftId: string;

  enrolments: IShiftEnrolment[];
  personsMap: { [key: string]: IPerson };

  actions: {
    getPersonsByIds: Function;
    removeShiftEnrolment: (
      shiftId: string,
      personId: string,
    ) => Promise<boolean>;
    updateShiftEnrolment: (
      enrolment: IShiftEnrolment,
    ) => Promise<IShiftEnrolment>;
  };
}

interface IShiftPersonsEditorState {
  personToEdit: null | string;
  error?: string | null;
  searchTerm: string;
  roleFilter: Role;
}

class ShiftPersonsEditor extends React.PureComponent<
  IShiftPersonsEditorProps,
  IShiftPersonsEditorState
> {
  tableRef: any;

  constructor(props, content) {
    super(props, content);
    this.state = {
      personToEdit: null,
      searchTerm: "",
      roleFilter: Role.none,
    };

    this.tableRef = React.createRef();

    this.onDelete = this.onDelete.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.onRoleEditorClick = this.onRoleEditorClick.bind(this);
    this.handleRoleEditorClose = this.handleRoleEditorClose.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.toggleRoleFilter = this.toggleRoleFilter.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
  }

  componentWillReceiveProps(newProps: IShiftPersonsEditorProps) {
    if (
      newProps.personToScrollTo !== this.props.personToScrollTo &&
      this.tableRef &&
      this.tableRef.current &&
      this.tableRef.current.children
    ) {
      const row = newProps.enrolments.findIndex(
        (e) => e.person === newProps.personToScrollTo,
      );
      if (row >= 0) {
        const child = this.tableRef.current.children[row];
        if (child)
          child.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  componentDidMount() {
    if (!this.props.enrolments || this.props.enrolments.length === 0) return;

    const store = this.props.personsMap || {};
    // get the ids that haven't been loaded yet
    const personIds = this.props.enrolments
      .filter((p) => !(p.person in store))
      .map((p) => p.person);
    this.props.actions.getPersonsByIds(personIds);
  }

  onUpdate(enrolment: IShiftEnrolment) {
    this.props.actions
      .updateShiftEnrolment(enrolment)
      .then((resp) => {
        this.setState({ personToEdit: null });
      })
      .catch((er) => {
        this.setState({ error: er.message });
      });
  }

  onDelete(personId) {
    this.props.actions
      .removeShiftEnrolment(this.props.shiftId, personId)
      .then((resp) => {
        this.setState({ personToEdit: null });
      })
      .catch((er) => {
        this.setState({ error: er.message });
      });
  }

  onRoleEditorClick(referee) {
    this.setState({ personToEdit: referee?.person });
  }

  handleRoleEditorClose() {
    this.setState({ personToEdit: null });
  }

  handleSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ searchTerm: event.currentTarget.value });
  }

  toggleRoleFilter(role: Role) {
    this.setState((prev) => {
      const hasRole = Role.hasRole(prev.roleFilter, role);
      const nextFilter = hasRole ? Role.removeRole(prev.roleFilter, role) : Role.addRole(prev.roleFilter, role);
      return { roleFilter: nextFilter };
    });
  }

  clearFilters() {
    this.setState({ searchTerm: "", roleFilter: Role.none });
  }

  render() {
    const { personsMap, enrolments } = this.props;
    const { searchTerm, roleFilter } = this.state;

    const sortedEnrolments = (enrolments || []).sort(shiftSorter(personsMap));

    const normalisedSearch = searchTerm.trim().toLowerCase();
    const selectedRoles = roleIconConfig
      .filter(({ role }) => Role.hasRole(roleFilter, role))
      .map(({ role }) => role);

    const filteredEnrolments = sortedEnrolments.filter((enrolment) => {
      const person = personsMap?.[enrolment.person];
      const fullName =
        person?.fullName ||
        [person?.firstName, person?.lastName].filter(Boolean).join(" ");
      const matchesName =
        !normalisedSearch ||
        (fullName ?? "")
          .toString()
          .toLowerCase()
          .includes(normalisedSearch);

      const matchesRole =
        selectedRoles.length === 0 ||
        selectedRoles.some((role) => Role.hasRole(enrolment.role, role));

      return matchesName && matchesRole;
    });

    const hasActiveFilters = normalisedSearch.length > 0 || roleFilter !== Role.none;
    const summaryLabel = `${filteredEnrolments.length} of ${sortedEnrolments.length} enrolments`;

    const personCells = filteredEnrolments.map((p, idx) => {
      if (!p) {
        return null;
      }
      const person = personsMap?.[p.person];

      const cell =
        p.person == this.state.personToEdit
          ? enrolmentCells.PersonRoleEditCell
          : enrolmentCells.PersonRoleCell;

      return React.createElement(cell, {
        key: idx,
        person,
        pid: p.person,
        referee: p,
        onDelete: this.onDelete,
        onChange: this.onUpdate,
        toggleEdit: this.onRoleEditorClick,
      });
    }) || (
        <p>
          <em>No persons</em>
        </p>
      );

    return (
      <div style={styles.maxVertical}>
        <div style={styles.filtersContainer}>
          <TextInput
            label="Filter by name"
            placeholder="Search by official name"
            value={searchTerm}
            onChange={this.handleSearchChange}
          />
          <div>
            <Group gap="xs" style={styles.roleFilters}>
              {roleIconConfig.map(({ role, label, icon }) => {
                const active = Role.hasRole(roleFilter, role);
                return (
                  <Tooltip key={role} label={label} withArrow withinPortal>
                    <button
                      onClick={() => this.toggleRoleFilter(role)}
                      style={{
                        border: active
                          ? "1px solid var(--color-brand-medium)"
                          : "1px solid transparent",
                        backgroundColor: "transparent",
                        borderRadius: "10px",
                        padding: "6px 10px",
                        cursor: "pointer",
                        color: active ? roleIconColors.active : roleIconColors.inactive,
                      }}
                      type="button"
                    >
                      <FontAwesomeIcon icon={icon} style={{ fontSize: "1rem" }} />
                    </button>
                  </Tooltip>
                );
              })}
              {hasActiveFilters && (
                <Button variant="subtle" size="xs" onClick={this.clearFilters}>
                  Clear filters
                </Button>
              )}
            </Group>
          </div>
          <div style={styles.filterSummary}>{summaryLabel}</div>
        </div>
        {this.state.error && (
          <Alert color="red" toggle={() => this.setState({ error: null })}>
            {this.state.error}
          </Alert>
        )}
        {filteredEnrolments.length === 0 ? (
          <Alert color="gray" variant="light">
            No enrolments match the current filters.
          </Alert>
        ) : (
          <SSTable style={styles.root} id="shift-person-editor">
            <tbody ref={this.tableRef}>{personCells}</tbody>
          </SSTable>
        )}
      </div>
    );
  }
}

const shiftSorter =
  (personsMap: { [key: string]: IPerson }) =>
    (person1: IShiftEnrolment, person2: IShiftEnrolment) => {
      if (!person1 || !person2) return 0;

      // lower number goes lower
      const typeRanks = [
        ShiftEnrolmentType.permanent,
        ShiftEnrolmentType.casual,
        ShiftEnrolmentType.emergency,
      ];
      const roleRanks = [
        Role.administrative,
        Role.supervisor,
        Role.refereeCoach,
        Role.statistician,
        Role.referee,
        Role.scoretable,
      ];

      let l = typeRanks.length;
      for (var index = 0; index < l; ++index) {
        const t = typeRanks[index];
        const isAType = person1.type == t;
        const isBType = person2.type == t;

        if (isAType && !isBType) return -1;
        if (!isAType && isBType) return 1;
      }

      l = roleRanks.length;
      for (var index = 0; index < l; ++index) {
        const r = roleRanks[index];
        const aRole = Role.hasRole(person1.role, r);
        const bRole = Role.hasRole(person2.role, r);

        if (aRole && !bRole) return -1;
        if (!aRole && bRole) return 1;
      }

      // else just return based on alphabetic order of last name
      const lP1 = personsMap[person1.person]?.lastName;
      const lP2 = personsMap[person2.person]?.lastName;
      if (!lP1 || !lP2) return 0;
      return lP1 < lP2 ? -1 : 1;
    };

function mapStateToProps(state: IState, ownProps: IShiftPersonsEditorProps) {
  return {
    personsMap: state.persons.map,
    enrolments: state.shiftEnrolments.byShiftId[ownProps.shiftId]?.map(
      (k) => state.shiftEnrolments.map[k],
    ),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        getPersonsByIds,
        removeShiftEnrolment,
        updateShiftEnrolment,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ShiftPersonsEditor);
